/* Shared navigation for Shopify and the local storefront preview. */
(() => {
  'use strict';
  if (customElements.get('cellini-navigation')) return;

  const focusable = 'a[href], button, input, select, textarea, summary, [tabindex]';

  function lockScroll() {
    const saved = [document.documentElement, document.body].map((element) => ({
      element,
      properties: ['overflow-x', 'overflow-y'].map((name) => ({
        name,
        value: element.style.getPropertyValue(name),
        priority: element.style.getPropertyPriority(name),
      })),
    }));
    for (const { element, properties } of saved) {
      for (const { name } of properties) element.style.setProperty(name, 'hidden', 'important');
    }
    return () => {
      for (const { element, properties } of saved) {
        for (const { name, value, priority } of properties) {
          if (value) element.style.setProperty(name, value, priority);
          else element.style.removeProperty(name);
        }
      }
    };
  }

  class CelliniNavigation extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.mode = this.dataset.navMode === 'mobile' ? 'mobile' : 'desktop';
      this.boundary = this.closest('[data-cellini-header], .header-wrapper, .preview-header') || this.parentElement;
      this.desktopMedia = matchMedia('(min-width: 990px)');
      this.hoverMedia = matchMedia('(min-width: 990px) and (hover: hover) and (pointer: fine)');
      this.groups = [...this.querySelectorAll('details[data-mega-menu]')];
      this.scrim = this.querySelector('[data-nav-scrim]');
      this.drawer = this.querySelector('dialog[data-nav-drawer]');
      this.opener = this.querySelector('[data-nav-open]');
      this.listen(window, 'resize', () => this.scheduleMeasure());
      this.listen(window, 'scroll', () => this.scheduleMeasure(), { passive: true });
      this.listen(this.desktopMedia, 'change', () => {
        this.close();
        this.scheduleMeasure();
      });
      if (typeof ResizeObserver === 'function' && this.boundary) {
        this.resizeObserver = new ResizeObserver(() => this.scheduleMeasure());
        this.resizeObserver.observe(this.boundary);
      }
      if (this.mode === 'mobile') this.bindMobile();
      else this.bindDesktop();
      this.syncState();
      this.scheduleMeasure();
    }

    disconnectedCallback() {
      this.close();
      this.controller?.abort();
      this.controller = null;
      this.resizeObserver?.disconnect();
      this.resizeObserver = null;
      clearTimeout(this.focusTimer);
      this.clearHoverTimers();
      cancelAnimationFrame(this.measureFrame);
      this.measureFrame = null;
    }

    listen(target, type, listener, options = {}) {
      target?.addEventListener(type, listener, { ...options, signal: this.controller.signal });
    }

    get isOpen() {
      return this.mode === 'mobile' ? Boolean(this.drawer?.open) : this.groups.some((group) => group.open);
    }

    clearHoverTimers() {
      clearTimeout(this.openTimer);
      clearTimeout(this.closeTimer);
    }

    scheduleMeasure() {
      if (this.measureFrame) return;
      this.measureFrame = requestAnimationFrame(() => {
        this.measureFrame = null;
        if (!this.isConnected || !this.boundary) return;
        const bottom = Math.max(0, Math.ceil(this.boundary.getBoundingClientRect().bottom));
        this.style.setProperty('--cellini-nav-bottom', `${bottom}px`);
        this.style.setProperty('--cellini-nav-max-height', `${Math.max(0, window.innerHeight - bottom - 16)}px`);
      });
    }

    closeOtherNavigation() {
      this.boundary?.querySelectorAll('cellini-navigation').forEach((navigation) => {
        if (navigation !== this && typeof navigation.close === 'function') navigation.close();
      });
    }

    syncState() {
      const open = this.isOpen;
      this.classList.toggle('is-open', open);
      this.setAttribute('data-nav-expanded', String(open));
      if (this.scrim) this.scrim.hidden = !open;
      if (this.opener) this.opener.setAttribute('aria-expanded', String(open));
      for (const group of this.groups) group.querySelector('summary')?.setAttribute('aria-expanded', String(group.open));

      if (open && !this.headerState) {
        const sticky = this.closest('sticky-header');
        const section = this.closest('.section-header');
        this.headerState = {
          sticky,
          hadPreventHide: sticky ? Object.prototype.hasOwnProperty.call(sticky, 'preventHide') : false,
          preventHide: sticky?.preventHide,
          section,
          hadMenuOpen: section?.classList.contains('menu-open'),
        };
        if (sticky) sticky.preventHide = true;
        section?.classList.add('menu-open');
      } else if (!open && this.headerState) {
        const { sticky, hadPreventHide, preventHide, section, hadMenuOpen } = this.headerState;
        if (sticky) {
          if (hadPreventHide) sticky.preventHide = preventHide;
          else delete sticky.preventHide;
        }
        if (!hadMenuOpen) section?.classList.remove('menu-open');
        this.headerState = null;
      }
    }

    bindDesktop() {
      for (const group of this.groups) {
        const summary = group.querySelector('summary');
        const panel = group.querySelector('.cellini-mega');
        if (summary && panel?.id) summary.setAttribute('aria-controls', panel.id);
        // A native summary activation takes precedence over a pending hover.
        // Otherwise, clicking an already-open summary shut can reopen it 120ms later.
        this.listen(summary, 'click', () => this.clearHoverTimers());
        this.listen(summary, 'keydown', (event) => {
          if (event.key === 'Enter' || event.key === ' ') this.clearHoverTimers();
        });
        this.listen(group, 'toggle', () => {
          if (group.open) {
            if (!this.desktopMedia.matches) group.open = false;
            else this.openGroup(group);
          }
          this.syncState();
        });
        this.listen(group, 'pointerenter', () => {
          if (!this.hoverMedia.matches) return;
          this.clearHoverTimers();
          this.openTimer = setTimeout(() => this.openGroup(group), 120);
        });
        this.listen(group, 'pointerleave', () => {
          if (!this.hoverMedia.matches) return;
          this.clearHoverTimers();
          this.closeTimer = setTimeout(() => {
            if (!group.contains(document.activeElement)) this.close();
          }, 150);
        });
      }
      this.listen(document, 'keydown', (event) => {
        if (event.key !== 'Escape') return;
        // Hover does not move focus into the navigation. Escape must still close
        // its panel, and cancel an opening delay even before a panel is visible.
        this.clearHoverTimers();
        if (!this.isOpen) return;
        event.preventDefault();
        event.stopPropagation();
        this.close(true);
      });
      this.listen(this, 'focusout', () => {
        clearTimeout(this.focusTimer);
        this.focusTimer = setTimeout(() => {
          if (!this.contains(document.activeElement)) this.close();
        });
      });
      this.listen(document, 'click', (event) => {
        if (!this.contains(event.target)) this.close();
      });
      this.listen(this.scrim, 'click', () => this.close());
      this.listen(this, 'click', (event) => {
        if (event.target instanceof Element && event.target.closest('a[href]')) this.close();
      });
    }

    openGroup(group) {
      if (!this.desktopMedia.matches || !this.isConnected) return;
      this.clearHoverTimers();
      this.closeOtherNavigation();
      for (const other of this.groups) other.open = other === group;
      this.syncState();
      this.scheduleMeasure();
    }

    bindMobile() {
      if (!this.drawer || !this.opener) return;
      this.opener.setAttribute('aria-haspopup', 'dialog');
      if (this.drawer.id) this.opener.setAttribute('aria-controls', this.drawer.id);
      this.listen(this.opener, 'click', () => this.openDrawer());
      this.listen(this.drawer, 'click', (event) => {
        const element = event.target instanceof Element ? event.target : null;
        if (element?.closest('[data-nav-close]')) {
          event.preventDefault();
          this.close(true);
        } else if (element?.closest('a[href]')) {
          this.close();
        } else if (this.backdropClick && this.isBackdrop(event)) {
          this.close(true);
        }
        this.backdropClick = false;
      });
      this.listen(this.drawer, 'pointerdown', (event) => {
        this.backdropClick = false;
        this.backdropPointer = event.isPrimary && event.button === 0 && this.isBackdrop(event) ? event.pointerId : null;
      });
      this.listen(this.drawer, 'pointerup', (event) => {
        this.backdropClick = this.backdropPointer === event.pointerId && this.isBackdrop(event);
        this.backdropPointer = null;
      });
      this.listen(this.drawer, 'pointercancel', () => {
        this.backdropPointer = null;
        this.backdropClick = false;
      });
      this.listen(this.drawer, 'submit', () => this.close());
      this.listen(this.drawer, 'cancel', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.close(true);
      });
      this.listen(this.drawer, 'close', () => {
        // An old queued close event must not close a newly reopened dialog.
        if (!this.drawer.open) this.finishDrawer(true);
      });
      this.listen(this.drawer, 'keydown', (event) => this.keepFocus(event));
      this.listen(this.drawer, 'toggle', (event) => {
        const category = event.target;
        if (!(category instanceof HTMLDetailsElement) || !category.matches('[data-mobile-category]')) return;
        if (category.open) {
          this.drawer.querySelectorAll('details[data-mobile-category]').forEach((other) => {
            if (other !== category) other.open = false;
          });
        }
        category.querySelector('summary')?.setAttribute('aria-expanded', String(category.open));
      }, { capture: true });
      this.drawer.querySelectorAll('details[data-mobile-category]').forEach((category) => {
        category.querySelector('summary')?.setAttribute('aria-expanded', String(category.open));
      });
    }

    isBackdrop(event) {
      if (event.target !== this.drawer) return false;
      const panel = this.drawer.querySelector('.cellini-nav-drawer__panel') || this.drawer;
      const bounds = panel.getBoundingClientRect();
      return event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom;
    }

    openDrawer() {
      if (this.desktopMedia.matches || !this.drawer || this.drawer.open || typeof this.drawer.showModal !== 'function') return;
      this.closeOtherNavigation();
      this.drawerState = { unlockScroll: lockScroll() };
      try {
        this.drawer.showModal();
        this.syncState();
        this.drawer.querySelector('[data-nav-close]')?.focus({ preventScroll: true });
      } catch (error) {
        this.finishDrawer();
        throw error;
      }
    }

    keepFocus(event) {
      if (event.key !== 'Tab' || !this.drawer.open) return;
      const items = [...this.drawer.querySelectorAll(focusable)].filter((element) => element.tabIndex >= 0
        && !element.disabled && !element.closest('[inert]') && element.getClientRects().length
        && getComputedStyle(element).visibility !== 'hidden');
      if (!items.length) {
        event.preventDefault();
        this.drawer.focus();
        return;
      }
      const first = items[0], last = items[items.length - 1];
      const focusOutsideItems = document.activeElement === this.drawer || !this.drawer.contains(document.activeElement);
      if (event.shiftKey && (document.activeElement === first || focusOutsideItems)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || focusOutsideItems)) {
        event.preventDefault();
        first.focus();
      }
    }

    finishDrawer(returnFocus = false) {
      const state = this.drawerState;
      if (!state) return;
      this.drawerState = null;
      state.unlockScroll();
      this.syncState();
      if (returnFocus && this.opener?.isConnected && !this.opener.disabled) this.opener.focus({ preventScroll: true });
    }

    // StickyHeader calls close() without stealing focus from the current page.
    close(returnFocus = false) {
      this.clearHoverTimers();
      if (this.mode === 'mobile') {
        if (this.drawer?.open) this.drawer.close();
        this.finishDrawer(returnFocus);
      } else {
        const summary = this.groups?.find((group) => group.open)?.querySelector('summary');
        for (const group of this.groups || []) group.open = false;
        this.syncState();
        if (returnFocus && summary?.isConnected) summary.focus({ preventScroll: true });
      }
    }
  }

  customElements.define('cellini-navigation', CelliniNavigation);
})();
